// SYSTEM-AGENT (Andrei, 2026-10-29): a platform super admin's private space with a company's
// system agent (Skillsmith) — from the operator console (`/operator/system-agents/{co}/{id}`) or
// from the company's context while acting there (`/c/{slug}/system-agents/{id}`). Ask "what are
// the issues?" or hand it issues to look into; it answers here and reports back on the
// investigations it opens. Stored platform-side; the API answers 404 to anyone but a super admin,
// and company users never see an entry for it.
import {
  Badge,
  Button,
  EmptyState,
  LocationHeader,
  NavList,
  NavRow,
  SidebarCaption,
  Textarea,
  WorkerBadge,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { CpuIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import { operatorSystemAgentsQuery, opsKeys, systemAgentSpaceQuery } from "../ops-queries";
import { OpsBody, OpsFrame, operatorCrumb, RowSub } from "../ops-ux";
import { LoadError, Loading } from "../screen-parts";

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

/**
 * The chat sidebar's "Platform" section in a company the viewer acts in as an operator: one row
 * per system agent of this company whose space the viewer (a super admin) may open. Renders
 * nothing for anyone else — the list answers `can_talk: false`, and company users never reach it.
 */
export function SuperAdminSpaces({
  api,
  companyId,
  slug,
}: {
  api: Api;
  companyId: string;
  slug: string;
}) {
  const q = useQuery(operatorSystemAgentsQuery(api));
  const rows = (q.data?.data ?? []).filter((r) => r.company.id === companyId && r.can_talk);
  if (rows.length === 0) return null;
  return (
    <>
      <SidebarCaption label="Platform (private)" />
      <NavList data-slot="sidebar-system-agents">
        {rows.map((r) => (
          <NavRow
            key={r.membership_id}
            icon={CpuIcon}
            label={r.name}
            render={<Link to="/c/$slug/system-agents/$id" params={{ slug, id: r.membership_id }} />}
          />
        ))}
      </NavList>
    </>
  );
}

export function SystemAgentSpace({
  api,
  companyId,
  membershipId,
  inCompany = false,
}: {
  api: Api;
  companyId: string;
  membershipId: string;
  /** Shown inside the company's context (no operator crumb). */
  inCompany?: boolean;
}) {
  const q = useQuery(systemAgentSpaceQuery(api, companyId, membershipId));
  const client = useQueryClient();
  const [draft, setDraft] = useState("");
  const send = useMutation({
    mutationFn: (body: string) =>
      api.client.operator.systemAgents.space.post({
        params: { company_id: companyId, membership_id: membershipId },
        body: { body },
      }),
    onSuccess: () => {
      setDraft("");
      void client.invalidateQueries({
        queryKey: opsKeys.systemAgentSpace(companyId, membershipId),
      });
    },
  });
  const s = q.data;
  const name = s?.name ?? "System agent";
  return (
    <OpsFrame>
      <LocationHeader
        crumbs={
          inCompany
            ? [{ label: name }]
            : [operatorCrumb, { label: "System agents" }, { label: name }]
        }
        icon={CpuIcon}
        title={name}
        actions={<Badge variant="outline">Private · super admins</Badge>}
      />
      <OpsBody>
        {q.isPending ? (
          <Loading label="Loading the conversation" />
        ) : q.isError || !s ? (
          <LoadError what="The conversation" error={q.error} />
        ) : (
          <div className="flex flex-col gap-4" data-slot="system-agent-space">
            <RowSub>
              {s.company.name}
              {s.pending ? ` · ${s.pending} waiting for an answer` : ""}
            </RowSub>
            {s.investigations.length ? (
              <ul className="flex flex-wrap gap-2" aria-label="Investigations">
                {s.investigations.map((i) => (
                  <li key={i.id}>
                    <Badge variant={i.state === "open" ? "warning" : "success"} title={i.body}>
                      {i.state === "open" ? "Investigating" : "Reported"}: {i.body.slice(0, 40)}
                    </Badge>
                  </li>
                ))}
              </ul>
            ) : null}
            {s.messages.length === 0 ? (
              <EmptyState
                variant="card"
                icon={CpuIcon}
                title="No messages yet"
                description='Ask "what are the issues?" or tell it what to look into.'
              />
            ) : (
              <ol className="flex flex-col gap-3">
                {s.messages.map((m) => (
                  <li key={m.id} className="flex flex-col gap-1" data-author={m.author_kind}>
                    <div className="flex items-center gap-2">
                      {m.author_kind === "system_agent" ? (
                        <WorkerBadge kind="system" size="sm" />
                      ) : (
                        <WorkerBadge kind="person" size="sm" />
                      )}
                      <span className="font-medium">
                        {m.author_kind === "system_agent" ? name : "Super admin"}
                      </span>
                      {m.kind !== "message" ? <Badge variant="outline">{m.kind}</Badge> : null}
                      <RowSub>{when(m.created_at)}</RowSub>
                    </div>
                    <p className="whitespace-pre-wrap text-sm">{m.body}</p>
                  </li>
                ))}
              </ol>
            )}
            <form
              className="flex flex-col gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (draft.trim()) send.mutate(draft.trim());
              }}
            >
              <Textarea
                aria-label={`Message ${name}`}
                placeholder="What are the issues? — or: these are the issues, go see why"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
              />
              <div>
                <Button type="submit" size="sm" disabled={!draft.trim() || send.isPending}>
                  Send
                </Button>
              </div>
            </form>
          </div>
        )}
      </OpsBody>
    </OpsFrame>
  );
}
